import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2Icon, LandmarkIcon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import {
  formatNumericInput, numericInputValue, parseNumericInput,
} from '@/lib/currency'
import type { CaptureExtras, ReportingBasis } from '@/types/capture'
import type { FinancialYear } from '@/types/deal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * FIELDS-1 P0 — proof-of-cash intake.
 *
 * The bank side of the corroboration comparator could only ever be fed by
 * ingested statements (POST /tax/bank-deposits). A deal whose deposits arrive as
 * a summary schedule, or whose statements are unreadable, had no way in at all —
 * so the card below stayed permanently "not yet provided" and the analyst had
 * nowhere to record what they knew. This is that way in.
 *
 * It sits next to its output rather than in the Normalization workspace on
 * purpose: the pending card is what tells the analyst something is missing, and
 * the fix should be one scroll away from the complaint. Persistence is still the
 * normalization session (form_extras_json), which is why the PUT below is the
 * ordinary normalization save.
 */

const CY = new Date().getFullYear()
const FALLBACK_YEARS = [CY - 2, CY - 1, CY]

interface NormalizationEnvelope {
  financial_years?: FinancialYear[]
  form_extras_json?: CaptureExtras | null
}

interface Props {
  dealId: string
  /** Called after a successful save so the caller can re-read the score. */
  onSaved?: () => void
}

/** The fiscal years to collect: whatever the deal's P&L actually holds, so the
 *  columns line up with the periods being corroborated. A deal with no P&L yet
 *  falls back to the rolling 3-year window (never hardcoded years). */
function yearsFrom(rows: FinancialYear[] | undefined): number[] {
  const years = (rows ?? [])
    .map(r => r.year)
    .filter((y): y is number => typeof y === 'number')
  return years.length > 0 ? [...new Set(years)].sort((a, b) => a - b) : FALLBACK_YEARS
}

export function ProofOfCashInputs({ dealId, onSaved }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [years, setYears] = useState<number[]>(FALLBACK_YEARS)
  const [basis, setBasis] = useState<ReportingBasis | ''>('')
  const [declared, setDeclared] = useState('')
  // Deposit fields hold the display string, keyed by year. Same reason as the
  // P&L grid: a controlled number input eats a trailing "." or "0" as it is
  // typed, so "1,200.05" is impossible to enter.
  const [deposits, setDeposits] = useState<Record<number, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<NormalizationEnvelope>(
        `/qoe/deals/${dealId}/normalization`,
      )
      const extras = res.data.form_extras_json ?? {}
      const fyYears = yearsFrom(res.data.financial_years)
      setYears(fyYears)
      setBasis(extras.reporting_basis ?? '')
      setDeclared(
        extras.declared_bank_accounts != null ? String(extras.declared_bank_accounts) : '',
      )
      const bank = extras.bank_periods ?? {}
      setDeposits(Object.fromEntries(fyYears.map(y => {
        const val = bank[String(y)]?.bank_deposits
        return [y, val != null ? numericInputValue(val) : '']
      })))
    } catch {
      // A deal with no normalization session yet is the ordinary early state,
      // not a failure — the form simply opens empty.
      setYears(FALLBACK_YEARS)
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => { void load() }, [load])

  const save = async () => {
    setSaving(true)
    try {
      // Only the capture fields. financial_years / adjustments are omitted on
      // purpose: the save route is preserve-on-omit, so this cannot disturb the
      // analyst's P&L grid, and form_extras_json is merged rather than replaced.
      await apiClient.put(`/qoe/deals/${dealId}/normalization`, {
        // '' means "the analyst has not stated a basis" — send nothing rather
        // than guessing, and let the handler caveat its accrual assumption.
        ...(basis ? { reporting_basis: basis } : {}),
        ...(declared.trim() !== '' && Number.isFinite(Number(declared))
          ? { declared_bank_accounts: Math.trunc(Number(declared)) }
          : {}),
        bank_periods: years.map(y => ({
          year: y,
          // '' clears the year: the handler stores null, and a null deposit is
          // skipped rather than compared against as $0.
          bank_deposits: parseNumericInput(deposits[y] ?? '') === ''
            ? null
            : parseNumericInput(deposits[y] ?? ''),
        })),
      })
      toast.success('Proof-of-cash inputs saved')
      onSaved?.()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <LandmarkIcon className="h-4 w-4 text-brand-primary" />
        <span className="font-medium text-sm text-brand-text">Proof of Cash — Analyst Input</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Enter what the statements show if they were not ingested as files. Figures
        keyed in here are labelled &quot;entered by analyst&quot; in the reconciliation
        below; a year whose statements were ingested keeps the ingested total.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="poc-basis" className="text-xs font-medium text-brand-text">
            Reporting basis
          </label>
          <select
            id="poc-basis"
            value={basis}
            onChange={e => setBasis(e.target.value as ReportingBasis | '')}
            className="h-8 rounded border border-input bg-transparent px-2 text-xs outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Not stated (accrual assumed)</option>
            <option value="accrual">Accrual</option>
            <option value="cash">Cash</option>
          </select>
          <span className="text-[11px] text-muted-foreground">
            Cash basis carries tighter deposit-gap thresholds than accrual.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="poc-declared" className="text-xs font-medium text-brand-text">
            Bank accounts declared by seller
          </label>
          <Input
            id="poc-declared"
            type="text"
            inputMode="numeric"
            value={declared}
            placeholder="e.g. 3"
            onChange={e => setDeclared(e.target.value.replace(/[^\d]/g, ''))}
            className="h-8 text-xs tabular-nums"
          />
          <span className="text-[11px] text-muted-foreground">
            Left blank, completeness stays UNVERIFIED — the card cannot claim a
            complete reconciliation without knowing the expected count.
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-3 font-normal">Fiscal year</th>
              <th className="py-1 pr-3 font-normal text-right">Total bank deposits</th>
            </tr>
          </thead>
          <tbody>
            {years.map(y => (
              <tr key={y} className="border-b last:border-0">
                <td className="py-1.5 pr-3">FY{y}</td>
                <td className="py-1.5 pr-3 text-right">
                  <Input
                    type="text"
                    inputMode="decimal"
                    aria-label={`FY${y} total bank deposits`}
                    value={deposits[y] ?? ''}
                    placeholder="0.00"
                    onChange={e => setDeposits(prev => ({
                      ...prev, [y]: formatNumericInput(e.target.value),
                    }))}
                    className="h-7 w-36 text-xs text-right tabular-nums ml-auto"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} className="gap-1.5 text-xs">
          {saving && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
          Save proof-of-cash inputs
        </Button>
      </div>
    </div>
  )
}

export default ProofOfCashInputs
